import { CheckIcon, ChevronLeftIcon } from '@heroicons/react/outline';
import {
  Affix,
  Button,
  ConfigProvider,
  DatePicker,
  Progress,
  theme,
  ThemeConfig,
  Tooltip,
} from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { Dispatch, SetStateAction, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';

import { EventDetails, logEvent } from '@/hooks/useClickEventTracking';

import RenderIf from '@/components/Common/RenderIf';
import { montserrat } from '@/components/Layout';

import { user } from '@/atoms/user';
import { saveOnboardingAnswer } from '@/services/Onboarding';
import { getUserProfile } from '@/services/user';
import {
  IOnboarding,
  IOnboardingStep,
  ISaveOnboardingAnswerPayload,
} from '@/utils/interfaces/Onboarding';
import { antdTheme } from '@/utils/Theme';

import CompleteOnboarding from './CompleteOnboarding';
import OnboardingStart from './OnboardingStart';
import CustomTooltip from '../shared/CustomTooltip';
import ButtonDefault, {
  ButtonVariants,
} from '../../components/shared/basic/button';
import { notify } from '../../components/shared/basic/notify';
import { ShouldRender } from '../../components/shared/basic/ShouldRender';
import { formateErrorObject } from '../../components/shared/formateErrorObject';
import CustomLoader from '../../components/shared/Loading';
import { ZebraLearnLogo } from '../../../assets';
import LogoSquare from '../../../assets/Icons/LogoSquare';

const config: ThemeConfig = {
  token: { colorPrimary: '#FFFFFF', fontFamily: 'inherit' },
};

const TypingEffect = ({ text }: { text: string }) => {
  return (
    <motion.p
      className=' min-h-full w-full z-0 flex justify-start items-center font-bold text-sm'
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      style={{
        fontFamily: montserrat.style.fontFamily,
      }}
    >
      {text}
    </motion.p>
  );
};

interface Props {
  onboarding: IOnboarding;
  onboardingCurrentStep: number;
  setOnboardingCurrentStep: Dispatch<SetStateAction<number>>;
}

const OnboardingCompQuestions: React.FC<Props> = ({
  onboarding,
  onboardingCurrentStep,
  setOnboardingCurrentStep,
}) => {
  const screens = useBreakpoint();
  const containerRef = useRef<HTMLDivElement>(null);

  const [loading] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [submitResponseLoading] = useState(false);

  const [onboardingData, setOnboardingData] = useState<IOnboarding>(onboarding);
  const [userData, setUserData] = useRecoilState(user);

  const router = useRouter();

  const onChange = (value: dayjs.Dayjs | null) => {
    if (value) {
      const today = dayjs().startOf('day');

      if (value.isAfter(today)) {
        console.error(
          'Selected date is a future date:',
          value.format('YYYY-MM-DD'),
        );
        notify(`Selected date is a future date: ${value.format('YYYY-MM-DD')}`);
        return;
      }
      setOnboardingData((prev) => {
        const updatedSteps = prev.steps.map((step) => {
          if (step.id === steps[onboardingCurrentStep]?.id) {
            return {
              ...step,
              answer: { value: value.format('YYYY-MM-DD') },
            };
          }
          return step;
        });

        return {
          ...prev,
          steps: updatedSteps,
        };
      });
    } else {
      setOnboardingData((prev) => {
        const updatedSteps = prev.steps.map((step) => {
          if (step.id === steps[onboardingCurrentStep]?.id) {
            return {
              ...step,
              answer: null,
            };
          }
          return step;
        });

        return {
          ...prev,
          steps: updatedSteps,
        };
      });
    }
  };

  const saveOnboardingAnswerHandle = async (
    data: Partial<ISaveOnboardingAnswerPayload>,
  ) => {
    try {
      setPostLoading(true);
      await saveOnboardingAnswer(data);
    } catch (error: any) {
      notify(formateErrorObject(error));
      throw error;
    } finally {
      setPostLoading(false);
    }
  };

  const saveHandle = async () => {
    try {
      const currentStep = steps[onboardingCurrentStep];
      const stepData = onboardingData.steps.find(
        (i) => i.id === currentStep?.id,
      );
      let payload: Partial<ISaveOnboardingAnswerPayload> = {};

      if (currentStep?.type === 'finish') {
        router.push('/');
        return;
      }

      if (currentStep?.type === 'single_choice') {
        payload = {
          value: stepData?.answer?.value,
          step_key: currentStep?.key,
        };
      }
      if (currentStep?.type === 'yes_no') {
        payload = {
          value: stepData?.answer?.value,
          step_key: currentStep?.key,
        };
      }
      if (currentStep?.type === 'text') {
        payload = {
          value: stepData?.answer?.value,
          step_key: currentStep?.key,
        };
      }

      if (currentStep?.type === 'date') {
        payload = {
          value: stepData?.answer?.value,
          step_key: currentStep?.key,
        };
      }

      if (currentStep?.type === 'multi_choice') {
        payload = {
          value: stepData?.answer?.value,
          step_key: currentStep?.key,
        };
      }

      if (
        ['date', 'single_choice', 'multi_choice', 'yes_no', 'text'].includes(
          currentStep?.type || '',
        )
      ) {
        await saveOnboardingAnswerHandle(payload);
      }

      if (steps.length - 2 === onboardingCurrentStep) {
        try {
          setPostLoading(true);
          const data = await getUserProfile();
          setUserData(data?.data);
          const eventParams: EventDetails = {
            event_name: 'onboarding_complete',
            source: 'global',
            source_type: 'onboarding',
            source_id: null,
            sub_source: null,
            sub_source_id: null,
            unit: 'boolean',
            value: 1,
          };

          logEvent({
            eventDetails: eventParams,
            userData: userData,
            pathname: router.pathname,
          });
        } catch (error: any) {
          notify(formateErrorObject(error));
        } finally {
          setPostLoading(false);
        }
      }

      setOnboardingCurrentStep((prev) => prev + 1);
    } catch (error) {
      console.error(error);
    }
  };

  const steps: IOnboardingStep[] = [
    {
      answer: 'init',
      id: 0,
      is_required: true,
      key: 'init',
      options: [{ id: 0, label: '0', value: '0' }],
      title: 'init',
      subtitle: 'init',
      type: 'init',
    },
    ...onboarding.steps,
    {
      answer: 'finish',
      id: 0,
      is_required: true,
      key: 'finish',
      options: [{ id: 0, label: '0', value: '0' }],
      title: 'finish',
      subtitle: 'finish',
      type: 'finish',
    },
  ];

  const currentStepData = steps?.[onboardingCurrentStep];
  const apiCurrentStepData = onboardingData.steps.find(
    (i) => i.id === currentStepData?.id,
  );

  const renderProcess = (
    <div className=' z-40  static top-10 py-2  w-full  bg-bgColor md:w-full '>
      <span className='  flex justify-center w-full gap-4 items-center'>
        <ShouldRender
          check={
            onboardingCurrentStep !== 0 && onboardingCurrentStep < steps.length
          }
        >
          <div className='flex justify-start '>
            <Button
              type='text'
              size={screens.md ? 'middle' : 'small'}
              onClick={() => setOnboardingCurrentStep((prev) => prev - 1)}
              className='text-base  text-white bg-cartBgColor border-none  hover:text-[#e0dede] font-medium  rounded-lg flex items-center gap-2'
            >
              <ChevronLeftIcon className='md:w-5 w-3 h-3 md:h-5' />
            </Button>
          </div>
        </ShouldRender>
        <ShouldRender check={currentStepData?.type !== 'init'}>
          <div className='flex justify-center items-start flex-1'>
            <Progress
              percent={((onboardingCurrentStep + 1) / steps.length) * 100}
              size={screens.md ? 'default' : 'small'}
              strokeWidth={screens.md ? 8 : 6}
              showInfo={false}
              strokeColor='#ffffff'
            />
          </div>
        </ShouldRender>
        <ShouldRender check={currentStepData?.type !== 'init'}>
          <Button
            type='text'
            size={screens.md ? 'middle' : 'small'}
            onClick={() => router.push('/')}
            className='text-xs md:text-sm  text-white bg-cartBgColor border-none  hover:text-[#e0dede] font-medium  rounded-lg flex items-center gap-2'
          >
            Skip
          </Button>
        </ShouldRender>
      </span>
    </div>
  );

  const Subtitle = (
    <motion.p
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className='text-sm text-grayZl-600'
      style={{
        fontFamily: montserrat.style.fontFamily,
      }}
    >
      {steps[onboardingCurrentStep]?.subtitle}
    </motion.p>
  );

  return (
    <ConfigProvider theme={{ ...config, algorithm: [theme.darkAlgorithm] }}>
      <nav
        className={clsx(
          'flex py-2 fixed z-50 px-3 md:px-5 lg:px-12 border-b-2 border-borderDarkGray items-center justify-between w-full h-14 lg:min-h-16 top-0 bg-bgColor bg-opacity-75 backdrop-filter backdrop-blur-md',
        )}
      >
        <div className='flex  w-full  justify-start items-center cursor-pointer'>
          <span onClick={() => router.push('/')}>
            <Image
              src={ZebraLearnLogo}
              alt='Logo'
              priority
              className='object-contain w-[125px] h-[14px]'
            />
          </span>
        </div>
      </nav>

      <div
        ref={containerRef}
        id='containerRef'
        className='flex justify-center  bg-bgColor min-h-screen'
      >
        <div
          className={clsx(
            'xl:w-1/2',
            'pt-14 md:pt-16 pb-10 px-5 w-full md:w-2/3 ',
          )}
        >
          <ShouldRender check={!loading}>
            <div
              // key={onboardingCurrentStep} // important so framer knows step changed
              // initial={{ opacity: 0, y: 10 }}
              // animate={{ opacity: 1, y: 0 }}
              // exit={{ opacity: 0, y: -10 }}
              // transition={{ duration: 0.3 }}
              className={clsx(
                currentStepData?.type === 'init' &&
                'justify-center  items-center no-scrollbar',
                currentStepData?.type === 'finish' &&
                'justify-center min-h-[70vh] no-scrollbar',
                'flex flex-col  z-40 relative gap-7 pb',
              )}
            >
              <ShouldRender
                check={[
                  'single_choice',
                  'date',
                  'multi_choice',
                  'yes_no',
                  'text',
                ].includes(steps[onboardingCurrentStep]?.type || '')}
              >
                {screens.md ? (
                  renderProcess
                ) : (
                  <Affix offsetTop={55}>{renderProcess}</Affix>
                )}
              </ShouldRender>
              <ShouldRender
                check={steps[onboardingCurrentStep]?.type === 'init'}
              >
                <OnboardingStart />
              </ShouldRender>
              <ShouldRender
                check={[
                  'single_choice',
                  'multi_choice',
                  'date',
                  'yes_no',
                  'text',
                ].includes(steps?.[onboardingCurrentStep]?.type || '')}
              >
                <AnimatePresence mode='wait'>
                  <motion.span
                    key={onboardingCurrentStep} // important so framer knows step changed
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className={clsx('pt-7 lg:px-10 xl:px-20 md:pt-4 ')}
                  >
                    <div
                      // key={question.questionId}
                      className='pb-24 md:pb-0'
                    // ref={(el) => {
                    //   questionRefs.current[`${currentStep}-${index}`] = el;
                    // }}
                    >
                      <ShouldRender check={!screens.lg}>
                        <div className='md:pl-10 pb-24 flex  customToolTip'>
                          <Tooltip
                            placement='right'
                            className='customToolTip'
                            open={true}
                            color='#282829'
                            style={{
                              fontFamily: montserrat.style.fontFamily,
                            }}
                            title={
                              <div className='space-y-2'>
                                <TypingEffect
                                  text={
                                    steps[onboardingCurrentStep]?.title || ''
                                  }
                                />
                                {Subtitle}
                              </div>
                            }
                          >
                            <motion.div
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <LogoSquare width='24' />
                            </motion.div>
                          </Tooltip>
                        </div>
                      </ShouldRender>
                      <ShouldRender check={screens.lg}>
                        <div className='md:pl-10 pb-8 md:pb-12 justify-start flex'>
                          <div>
                            <CustomTooltip
                              text={
                                <div className='space-y-2'>
                                  <TypingEffect
                                    text={
                                      steps?.[onboardingCurrentStep]?.title ||
                                      ''
                                    }
                                  />
                                  {Subtitle}
                                </div>
                              }
                            >
                              <motion.div
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                <LogoSquare width='48' />
                              </motion.div>
                            </CustomTooltip>
                          </div>
                        </div>
                      </ShouldRender>

                      {/* Conditional rendering based on formType */}
                      {/* <ShouldRender
                      check={
                        steps?.[onboardingCurrentStep]?.type === 'multi_choice'
                      }
                    >
                      <div
                        className={clsx(
                          !errorMessage ? 'text-white' : 'text-red-500',
                          ' mb-4',
                        )}
                      >
                        You can select up to 5.
                      </div>
                    </ShouldRender> */}

                      <div
                        className={clsx(
                          currentStepData?.type === 'multi_choice'
                            ? 'flex-wrap flex gap-2'
                            : apiCurrentStepData?.answer?.value?.length < 3
                              ? 'grid md:grid-cols-2 gap-4'
                              : 'flex-col flex gap-4',
                        )}
                      >
                        {/* <div>
                        <div
                          className={clsx(
                            'rounded-lg flex py-2 px-3 z-10 lg:py-3 lg:px-4 xl:py-4 xl:px-6 w-fit items-center gap-2 cursor-pointer',
                            true
                              ? 'bg-[#151515] group-hover:bg-[#282829]'
                              : 'bg-[#282829] text-sm border-white border group-hover:bg-[#151515]',
                          )}
                        >
                          <label>{'option.text'}</label>

                          <label>Option</label>
                        </div>
                      </div> */}
                        <RenderIf condition={currentStepData?.type === 'text'}>
                          <motion.div
                            className='flex-1'
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 1.01 }}
                          >
                            <textarea
                              value={apiCurrentStepData?.answer?.value}
                              onChange={(e) => {
                                setOnboardingData((prev) => {
                                  const updatedSteps = prev.steps.map(
                                    (step) => {
                                      if (
                                        step.id ===
                                        steps[onboardingCurrentStep]?.id
                                      ) {
                                        return {
                                          ...step,
                                          answer: {
                                            value: e.target.value,
                                          },
                                        };
                                      }
                                      return step;
                                    },
                                  );

                                  return {
                                    ...prev,
                                    steps: updatedSteps,
                                  };
                                });
                              }}
                              className='h-[138px] focus-visible:outline-none  border-grayZl-900 border bg-black w-full rounded-lg p-3 sm:text-base text-sm placeholder:text-grayZl-600 text-white'
                              placeholder='Give Description'
                            ></textarea>
                          </motion.div>
                        </RenderIf>
                        <RenderIf
                          condition={currentStepData?.type === 'yes_no'}
                        >
                          <div className='flex sm:gap-[18px] gap-3 '>
                            <motion.div
                              className='flex-1'
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => {
                                setOnboardingData((prev) => {
                                  const updatedSteps = prev.steps.map(
                                    (step) => {
                                      if (
                                        step.id ===
                                        steps[onboardingCurrentStep]?.id
                                      ) {
                                        return {
                                          ...step,
                                          answer: {
                                            value: 'yes',
                                          },
                                        };
                                      }
                                      return step;
                                    },
                                  );

                                  return {
                                    ...prev,
                                    steps: updatedSteps,
                                  };
                                });
                              }}
                            >
                              <div
                                className={clsx(
                                  'w-full rounded-lg flex py-2 px-3 z-10 lg:py-3 lg:px-4 xl:py-4 xl:px-6  items-center gap-2 cursor-pointer text-sm',
                                  apiCurrentStepData?.answer?.value === 'yes'
                                    ? '  bg-[#282829]  border-white border group-hover:bg-[#151515]  '
                                    : ' bg-[#151515] group-hover:bg-[#282829] border border-black',
                                )}
                              >
                                <label>Yes</label>
                              </div>
                            </motion.div>
                            <motion.div
                              className='flex-1'
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => {
                                setOnboardingData((prev) => {
                                  const updatedSteps = prev.steps.map(
                                    (step) => {
                                      if (
                                        step.id ===
                                        steps[onboardingCurrentStep]?.id
                                      ) {
                                        return {
                                          ...step,
                                          answer: {
                                            value: 'no',
                                          },
                                        };
                                      }
                                      return step;
                                    },
                                  );

                                  return {
                                    ...prev,
                                    steps: updatedSteps,
                                  };
                                });
                              }}
                            >
                              <div
                                className={clsx(
                                  'w-full rounded-lg flex py-2 px-3 z-10 lg:py-3 lg:px-4 xl:py-4 xl:px-6  items-center gap-2 cursor-pointer text-sm',
                                  apiCurrentStepData?.answer?.value === 'no'
                                    ? '  bg-[#282829]  border-white border group-hover:bg-[#151515]  '
                                    : ' bg-[#151515] group-hover:bg-[#282829] border border-black',
                                )}
                              >
                                <label>No</label>
                              </div>
                            </motion.div>
                          </div>
                        </RenderIf>

                        {currentStepData?.type === 'multi_choice' &&
                          steps?.[onboardingCurrentStep]?.options?.map(
                            (option) => (
                              <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.9 }}
                                key={option.id}
                              >
                                <div
                                  className={clsx(
                                    'rounded-lg flex py-2 px-3 z-10 lg:py-3 lg:px-4 xl:py-4 xl:px-6 w-fit items-center gap-2 cursor-pointer text-sm',
                                    apiCurrentStepData?.answer?.value?.includes(
                                      option.id,
                                    )
                                      ? '  bg-[#282829]  border-white border group-hover:bg-[#151515]  '
                                      : ' bg-[#151515] group-hover:bg-[#282829] border border-black',
                                  )}
                                  onClick={() => {
                                    if (
                                      steps?.[onboardingCurrentStep]?.type ===
                                      'multi_choice'
                                    ) {
                                      // setOptionIds((prev) => {
                                      //   if (prev.includes(option.id)) {
                                      //     // remove
                                      //     return prev.filter(
                                      //       (item) => item !== option.id,
                                      //     );
                                      //   } else {
                                      //     // add
                                      //     return [...prev, option.id];
                                      //   }
                                      // });
                                      setOnboardingData((prev) => {
                                        const updatedSteps = prev.steps.map(
                                          (step) => {
                                            if (
                                              step.id ===
                                              steps[onboardingCurrentStep]?.id
                                            ) {
                                              const prevIds =
                                                step.answer?.value || [];

                                              const newIds = prevIds.includes(
                                                option.id,
                                              )
                                                ? prevIds.filter(
                                                  (id: any) =>
                                                    id !== option.id,
                                                ) // remove
                                                : [...prevIds, option.id]; // add

                                              return {
                                                ...step,
                                                answer: {
                                                  value: newIds,
                                                },
                                              };
                                            }
                                            return step;
                                          },
                                        );

                                        return {
                                          ...prev,
                                          steps: updatedSteps,
                                        };
                                      });
                                    }
                                    if (
                                      steps?.[onboardingCurrentStep]?.type ===
                                      'single_choice'
                                    ) {
                                      // setOptionId(option.id);
                                      setOnboardingData((prev) => {
                                        const updatedSteps = prev.steps.map(
                                          (step) => {
                                            if (
                                              step.id ===
                                              steps[onboardingCurrentStep]?.id
                                            ) {
                                              return {
                                                ...step,
                                                answer: {
                                                  option_id: option.id,
                                                },
                                              };
                                            }
                                            return step;
                                          },
                                        );

                                        return {
                                          ...prev,
                                          steps: updatedSteps,
                                        };
                                      });
                                    }
                                  }}
                                >
                                  <label>{option.label}</label>
                                </div>
                              </motion.div>
                            ),
                          )}

                        {currentStepData?.type === 'single_choice' &&
                          steps?.[onboardingCurrentStep]?.options?.map(
                            (option) => (
                              <span
                                key={option.id}
                                className='group z-0 w-full'
                              >
                                <motion.div
                                  onClick={() => {
                                    setOnboardingData((prev) => {
                                      const updatedSteps = prev.steps.map(
                                        (step) => {
                                          if (
                                            step.id ===
                                            steps[onboardingCurrentStep]?.id
                                          ) {
                                            return {
                                              ...step,
                                              answer: {
                                                value: option.id,
                                              },
                                            };
                                          }
                                          return step;
                                        },
                                      );

                                      return {
                                        ...prev,
                                        steps: updatedSteps,
                                      };
                                    });
                                  }}
                                  className={clsx(
                                    'py-3 px-3 md:px-5 rounded-lg z-10 cursor-pointer flex items-center justify-between gap-2',
                                    apiCurrentStepData?.answer?.value ===
                                      option.id
                                      ? 'bg-[#151515] group-hover:bg-[#282829]'
                                      : 'bg-[#282829] group-hover:bg-[#151515]',
                                  )}
                                  whileHover={{ scale: 1.008 }}
                                  whileTap={{ scale: 0.99 }}
                                >
                                  <div className='flex text-sm md:text-base items-center gap-2 w-full'>
                                    <div
                                      className={clsx(
                                        'md:w-5 h-3.5 w-3.5 md:h-5 md:min-w-5 md:min-h-5 border rounded-full flex items-center justify-center',

                                        apiCurrentStepData?.answer?.value ===
                                          option.id
                                          ? 'bg-white text-black'
                                          : 'border-[#515151] group-hover:border-[#3D3D3D]',
                                      )}
                                    >
                                      {steps?.[onboardingCurrentStep]?.type ===
                                        'single_choice' ? (
                                        onboardingData.steps.find(
                                          (i) =>
                                            i.id ===
                                            steps?.[onboardingCurrentStep]?.id,
                                        )?.answer?.value === option?.id && (
                                          <CheckIcon />
                                        )
                                      ) : (
                                        <></>
                                      )}
                                    </div>
                                    {option.label}
                                  </div>
                                </motion.div>
                              </span>
                            ),
                          )}

                        <ShouldRender
                          check={
                            steps?.[onboardingCurrentStep]?.type === 'date'
                          }
                        >
                          <div className='group z-0 w-full flex justify-center'>
                            <ConfigProvider theme={antdTheme}>
                              <DatePicker
                                placeholder='Select DOB'
                                value={
                                  apiCurrentStepData?.answer?.value
                                    ? dayjs(apiCurrentStepData.answer.value)
                                    : null
                                }
                                onChange={onChange}
                                format='YYYY-MM-DD'
                                className='max-w-60 w-full h-11'
                              />
                            </ConfigProvider>
                          </div>
                        </ShouldRender>
                      </div>
                    </div>
                  </motion.span>
                </AnimatePresence>
              </ShouldRender>

              <ShouldRender
                check={steps[onboardingCurrentStep]?.type === 'finish'}
              >
                <CompleteOnboarding />
              </ShouldRender>

              <div
                className={clsx(
                  currentStepData?.type === 'init'
                    ? 'flex flex-col items-center w-full gap-y-2'
                    : 'flex',
                  ' justify-center w-full gap-x-4    fixed bottom-0 left-0  p-5 bg-black z-50',
                )}
              >
                <ButtonDefault
                  size={screens.md ? 2 : 1}
                  rounded={false}
                  variant={
                    currentStepData?.type === 'init'
                      ? ButtonVariants.PRIMARY
                      : ButtonVariants.WHITE
                  }
                  enableScaling={false}
                  loading={postLoading}
                  disabled={postLoading}
                  onClick={() => {
                    saveHandle();
                  }}
                  is3D={['init', 'finish'].includes(
                    steps[onboardingCurrentStep]?.type || '',
                  )}
                  className={clsx(
                    steps[onboardingCurrentStep]?.type === 'init'
                      ? ' md:max-w-[570px]'
                      : steps[onboardingCurrentStep]?.type === 'finish'
                        ? 'md:max-w-[400px]'
                        : ' md:max-w-[350px]',
                    'w-full text-center text-sm p-3 max-w-[570px]',
                  )}
                >
                  <span className='md:px-1 py-1 font-semibold  whitespace-nowrap'>
                    {onboardingCurrentStep === 0
                      ? 'Get Started'
                      : steps.length - 2 === onboardingCurrentStep
                        ? 'Submit'
                        : 'Continue'}
                  </span>
                </ButtonDefault>
                <ShouldRender
                  check={['init'].includes(
                    steps[onboardingCurrentStep]?.type || '',
                  )}
                >
                  <ButtonDefault
                    size={screens.md ? 2 : 1}
                    rounded={false}
                    variant={ButtonVariants.GRAY}
                    enableScaling={false}
                    loading={submitResponseLoading}
                    disabled={submitResponseLoading}
                    onClick={() => router.push('/')}
                    className='w-full text-center text-sm p-3  max-w-[570px]'
                  >
                    <span className='md:px-1 py-1 font-semibold   whitespace-nowrap'>
                      Skip
                    </span>
                  </ButtonDefault>
                </ShouldRender>
              </div>
            </div>
          </ShouldRender>
        </div>

        <ShouldRender check={loading}>
          <CustomLoader />
        </ShouldRender>
      </div>
      {/* <SignUpModal
        onCancel={onCancelCloseModal}
        visible={visible.signUp}
        noRedirect={visible?.noRedirect}
      />
      <LoginModal onCancel={onCancelCloseLoginModal} visible={visible.login} />
      <ForgotPasswordModal
        onCancel={onCancelCloseForgotPasswordModal}
        visible={visible.forgotPassword}
      /> */}
    </ConfigProvider>
  );
};

export default OnboardingCompQuestions;
